import { v4 as uuid4 } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { VariableResultCustomText, VariableSelectFeatureFlag, VariableTextFeatureFlag } from '@makyoapp/makyo-common'

import { getDefaultDescendantValue } from '@/lib/slate'
import { FORM_VALIDATION_PATTERNS } from '@/shared/form-utils'

import {
  ALLOWED_FILE_TYPES,
  ALLOWED_IMAGE_TYPES,
  DEFAULT_COLUMN_TYPES,
  DEFAULT_FILE_TYPES,
  DEFAULT_IMAGE_TYPES,
} from '../constants'
import { BuilderComponentGroup, BuilderComponentSettingType, ComponentSettingKey } from '../types'
import { LABELS } from '../types/labels'
import { makeDataSource, makeFilter, makeSorting } from '../utils/components'
import { generateTimeOptions } from '../utils/misc'
import { getDefaultMargin } from '../viewport/utils'

export const FormComponents: BuilderComponentGroup = {
  id: 'form',
  title: 'Forms',
  components: [
    {
      compId: 'text-field',
      title: LABELS.TEXT_FIELD_KEY,
      searchKeywords: [
        'text field',
        'input',
        'textbox',
        'user entry',
        'text box',
        'form field',
        'single line input',
        'multiline input',
      ],
      icon: 'Textfield',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TextFieldDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                forceHorizontal: true,
                columnSelectSettings: {
                  // onValueChange: makeValueChangeCb(ComponentSettingKey.TextFieldDataLabel),
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.TextFieldDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.TextFieldDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.TextFieldDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Number,
                    ColumnTypes.Address,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Phone,
                    ColumnTypes.Rating,
                  ],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TextFieldDataLabel,
                label: LABELS.MAIN_LABEL_KEY,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
              {
                id: ComponentSettingKey.TextFieldDataPrefix,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
                autoDefaultInDetailPage: false,
                label: LABELS.PREFIX_KEY,
                help: 'Requires Small height',
                dependOn: {
                  id: ComponentSettingKey.TextFieldDesignHeight,
                  showWhen: (v) => v === 'small',
                },
              },
              {
                id: ComponentSettingKey.TextFieldDataSuffix,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
                autoDefaultInDetailPage: false,
                label: LABELS.SUFFIX_KEY,
                help: 'Requires Small height',
                dependOn: {
                  id: ComponentSettingKey.TextFieldDesignHeight,
                  showWhen: (v) => v === 'small',
                },
              },
              {
                id: ComponentSettingKey.TextFieldDataHelp,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
                autoDefaultInDetailPage: false,
                label: LABELS.HELP_TEXT_KEY,
                help: 'This label will replace the default help text shown for the selected pattern.',
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TextFieldDataDefaultValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: '',
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  bindForm: true,
                  formValidation: ComponentSettingKey.TextFieldBehaviorPattern,
                },
              },
              {
                id: ComponentSettingKey.TextFieldDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
          Icon: {
            items: [
              {
                id: ComponentSettingKey.TextFieldDataLeadingIcon,
                type: BuilderComponentSettingType.Icon,
                label: LABELS.LEADING_ICON_KEY,
              },
              // {
              //   id: ComponentSettingKey.TextFieldDataTrailingIcon,
              //   type: BuilderComponentSettingType.Icon,
              //   label: LABELS.TRAILING_ICON_KEY,
              // },
            ],
          },
        },
        designGroup: {
          [LABELS.HEIGHT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TextFieldDesignHeight,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HEIGHT_KEY,
                defaultValue: 'small',
                selectSettings: {
                  options: [
                    {
                      value: 'small',
                      label: 'Small',
                    },
                    {
                      value: 'medium',
                      label: 'Medium',
                    },
                    {
                      value: 'large',
                      label: 'Large',
                    },
                  ],
                },
              },
            ],
          },
          // 'Color Scheme': {
          //   items: [
          //     makeScheme(ComponentSettingKey.TextFieldDesignScheme),
          //     makeSchemeVariant(
          //       ComponentSettingKey.TextFieldDesignColorVariant,
          //       ComponentSettingKey.TextFieldDesignScheme
          //     ),
          //   ],
          // },
          // Style: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.TextFieldDesignOutlined,
          //       type: BuilderComponentSettingType.Switch,
          //       label: 'Outlined',
          //     },
          //   ],
          // },
          // Spacing: makeSpacing({
          //   margin: {
          //     id: ComponentSettingKey.TextFieldDesignMargin,
          //     defaultValue: { x: 10, y: 5 },
          //   },
          // }),
          // Typography: makeTypographyGroup(ComponentSettingKey.TextFieldDesignTypography, [
          //   {
          //     id: ComponentSettingKey.TextFieldDesignLabelTypography,
          //     label: 'Label',
          //     defaultValue: 'Body Large',
          //   },
          //   {
          //     id: ComponentSettingKey.TextFieldDesignInputTypography,
          //     label: 'Input',
          //     defaultValue: 'Body Large',
          //   },
          //   {
          //     id: ComponentSettingKey.TextFieldDesignHelpTypography,
          //     label: 'Help Text',
          //     defaultValue: 'Body Small',
          //   },
          // ]),
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.TextFieldBehaviorPattern,
                  label: LABELS.PATTERN_KEY,
                  type: BuilderComponentSettingType.Select,
                  selectSettings: {
                    multiline: true,
                    options: FORM_VALIDATION_PATTERNS.map((item) => ({
                      value: item.pattern,
                      label: item.label,
                    })),
                  },
                },
                {
                  id: ComponentSettingKey.TextFieldBehaviorPatternShowHelp,
                  label: LABELS.SHOW_VALIDATION_HELP_TEXT_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.TextFieldBehaviorMinChar,
                  label: LABELS.MIN_CHARACTERS_KEY,
                  type: BuilderComponentSettingType.Number,
                  defaultValue: 1,
                  /*dependOn: {
                    id: ComponentSettingKey.TextFieldBehaviorInputType,
                    showWhen: (v) => v !== 'number'
                  }*/
                },
                {
                  id: ComponentSettingKey.TextFieldBehaviorMaxChar,
                  label: LABELS.MAX_CHARACTERS_KEY,
                  type: BuilderComponentSettingType.Number,
                  defaultValue: 1000,
                  numberSettings: {
                    max: 1000,
                    min: 0,
                    step: 1,
                  },
                },
                {
                  id: ComponentSettingKey.TextFieldBehaviorRequired,
                  label: LABELS.REQUIRED_KEY,
                  type: BuilderComponentSettingType.Switch,
                },
              ],
            },
            [LABELS.SHOW_HIDE_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.TextFieldBehaviorPasswordMask,
                  label: 'Password Mask',
                  help: 'Requires Small height',
                  type: BuilderComponentSettingType.Switch,
                  dependOn: {
                    id: ComponentSettingKey.TextFieldDesignHeight,
                    showWhen: (v) => v === 'small',
                  },
                },
                {
                  id: ComponentSettingKey.TextFieldBehaviorCharacterCounter,
                  label: 'Character Counter',
                  help: 'Enable this will replace help text.',
                  type: BuilderComponentSettingType.Switch,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    /*{
      compId: 'multi-selection',
      title: 'Multi Selection',
      icon: 'Checkbox24',
      nested: false,
      isForm: true,
      //detailDataSource: undefined,
      settings: {
        dataGroup: {
          'Data Source': {
            items: [
              {
                id: ComponentSettingKey.MultiSelectionDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Value',
                columnSelectSettings: {
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Array, ColumnTypes.ImageArray]
                }
              }
            ]
          },
          'Options Data': {
            items: [
              makeDataSource(
                ComponentSettingKey.MultiSelectionDataOptionsSource,
                undefined,
                'Options Data Source'
              ),
              {
                id: ComponentSettingKey.MultiSelectionDataOptionsValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Options Value',
                help: 'Value represent the option. This is the value that will be read as. Each option must be unique value',
                columnSelectSettings: {
                  tableSourceId:
                    ComponentSettingKey.MultiSelectionDataOptionsSource
                }
              },
              {
                id: ComponentSettingKey.MultiSelectionDataOptionsLabel,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Options Label',
                help: 'Display text shown in the UI represent the option. If unspecified Options Value will be use',
                columnSelectSettings: {
                  tableSourceId:
                    ComponentSettingKey.MultiSelectionDataOptionsSource
                }
              }
            ]
          }
        },
        designGroup: {
          'Color Scheme': {
            items: [
              makeScheme(ComponentSettingKey.CheckboxDesignScheme),
              makeSchemeVariant(
                ComponentSettingKey.CheckboxDesignColorVariant,
                ComponentSettingKey.CheckboxDesignScheme
              )
            ]
          },
          Spacing: makeSpacing({
            margin: {
              id: ComponentSettingKey.CheckboxDesignMargin,
              defaultValue: { y: 5, x: 10 }
            }
          })
        },
        action: [
          {
            id: ComponentSettingKey.CheckboxActionValueOn,
            label: 'On Check',
            isSimple: false
          },
          {
            id: ComponentSettingKey.CheckboxActionValueOff,
            label: 'On Uncheck',
            isSimple: false
          }
        ],
        behavior: {
          visibility: true
        }
      }
    },*/
    // {
    //   compId: 'radio',
    //   title: LABELS.RADIO_BUTTON_KEY,
    //   icon: 'Radio24',
    //   nested: false,
    //   isForm: true,
    //   settings: {
    //     dataGroup: {
    //       [LABELS.DATA_SOURCE_KEY]: {
    //         items: [
    //           {
    //             id: ComponentSettingKey.RadioDataValue,
    //             type: BuilderComponentSettingType.ColumnSelect,
    //             label: LABELS.COLUMN_KEY,
    //             columnSelectSettings: {
    //               tableSourceId: '',
    //               bindForm: true,
    //               columnTypes: [
    //                 ColumnTypes.Text,
    //                 ColumnTypes.Number,
    //                 ColumnTypes.Address,
    //                 ColumnTypes.URL,
    //                 ColumnTypes.Email,
    //                 ColumnTypes.Phone,
    //                 ColumnTypes.Rating,
    //                 ColumnTypes.Color,
    //                 ColumnTypes.Barcode,
    //               ],
    //             },
    //           },
    //         ],
    //       },
    //       [LABELS.OPTIONS_DATA_KEY]: {
    //         items: [
    //           makeDataSource(ComponentSettingKey.RadioDataOptionsSource, undefined, LABELS.OPTIONS_DATA_SOURCE_KEY),
    //           {
    //             id: ComponentSettingKey.RadioDataOptionsValue,
    //             type: BuilderComponentSettingType.ColumnSelect,
    //             label: LABELS.OPTIONS_VALUE_KEY,
    //             help: LABELS.OPTIONS_VALUE_HELP_KEY,
    //             columnSelectSettings: {
    //               tableSourceId: ComponentSettingKey.RadioDataOptionsSource,
    //             },
    //           },
    //           {
    //             id: ComponentSettingKey.RadioDataOptionsLabel,
    //             type: BuilderComponentSettingType.ColumnSelect,
    //             label: LABELS.OPTIONS_LABEL_KEY,
    //             help: LABELS.OPTIONS_LABEL_HELP_KEY,
    //             columnSelectSettings: {
    //               tableSourceId: ComponentSettingKey.RadioDataOptionsSource,
    //             },
    //           },
    //         ],
    //       },
    //     },
    //     behavior: {
    //       visibility: true,
    //     },
    //   },
    // },
    {
      compId: 'checkbox',
      title: LABELS.CHECKBOX_KEY,
      searchKeywords: ['checkbox', 'tick box', 'select option', 'yes/no', 'multi-select', 'option box', 'check option'],
      icon: 'Checkbox24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CheckboxDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.CheckboxDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.CheckboxDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.CheckboxDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CheckboxDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CheckboxDataDefaultValue,
                label: '',
                type: BuilderComponentSettingType.VariableSelect,
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
              {
                id: ComponentSettingKey.CheckboxDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CheckboxBehaviorRequired,
                  label: LABELS.MUST_BE_CHECK,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: false,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'switch',
      title: LABELS.SWITCH_KEY,
      searchKeywords: ['switch', 'toggle', 'on/off', 'enable/disable', 'toggle button'],
      icon: 'Switch24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SwitchDataValue,
                label: LABELS.COLUMN_KEY,
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.SwitchDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.SwitchDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.SwitchDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SwitchDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SwitchDataDefaultValue,
                label: '',
                type: BuilderComponentSettingType.VariableSelect,
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
              {
                id: ComponentSettingKey.SwitchDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.SwitchBehaviorRequired,
                  label: LABELS.MUST_BE_SWITCHED_ON,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: false,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'field-rating',
      title: LABELS.RATING_KEY,
      searchKeywords: ['rating', 'stars', 'feedback', 'review', 'score', 'vote', 'star rating', '5-star'],
      icon: 'MiStarHalf24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FieldRatingDataValue,
                label: LABELS.COLUMN_KEY,
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.FieldRatingDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.FieldRatingDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.FieldRatingDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Rating],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FieldRatingDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FieldRatingDataDefaultValue,
                label: '',
                type: BuilderComponentSettingType.VariableSelect,
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.Rating],
                },
              },
              {
                id: ComponentSettingKey.FieldRatingDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.FieldRatingBehaviorRequired,
                  label: LABELS.REQUIRED_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: false,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'selection',
      title: LABELS.SELECTION_KEY,
      searchKeywords: [
        'dropdown',
        'select',
        'picker',
        'options',
        'radio button',
        'radio group',
        'checkbox group',
        'multi-select',
        'chips',
        'tags',
        'filter',
        'menu',
        'combo box',
        'option list',
        'choice field',
        'category picker',
      ],
      icon: 'Dropdown24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SelectionDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                forceHorizontal: true,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.SelectionDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.SelectionDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.SelectionDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Number,
                    ColumnTypes.Address,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Phone,
                    ColumnTypes.Rating,
                    ColumnTypes.Color,
                    ColumnTypes.Array,
                    ColumnTypes.ImageArray,
                  ],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SelectionDataLabel,
                label: LABELS.MAIN_LABEL_KEY,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
              {
                id: ComponentSettingKey.SelectionDataHelp,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
                label: LABELS.HELP_TEXT_KEY,
                defaultValue: '',
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SelectionDataDefaultValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: '',
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Number,
                    ColumnTypes.Address,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Phone,
                    ColumnTypes.Rating,
                    ColumnTypes.Color,
                  ],
                },
              },
              {
                id: ComponentSettingKey.SelectionDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
          [LABELS.GET_OPTIONS_FROM_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.SelectionDataOptionsSource, undefined, 'Options Data Source'),
              {
                id: ComponentSettingKey.SelectionDataOptionsValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.OPTIONS_KEY,
                help: 'Value represent the option. This is the value that will be read as. Each option must be unique value',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.SelectionDataOptionsSource,
                },
              },
              {
                id: ComponentSettingKey.SelectionDataOptionsLabel,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.DISPLAY_AS_KEY,
                help: 'Display text shown in the UI represent the option. If unspecified Options Value will be use',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.SelectionDataOptionsSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Number,
                    ColumnTypes.Rating,
                    ColumnTypes.Address,
                    ColumnTypes.Color,
                    ColumnTypes.DateTime,
                    ColumnTypes.Phone,
                    ColumnTypes.Email,
                    ColumnTypes.RowID,
                  ],
                },
              },
              {
                id: ComponentSettingKey.SelectionDataOptionsIcon,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.DISPLAY_AS_ICON_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.SelectionDataOptionsSource,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Icon, ColumnTypes.URL, ColumnTypes.Lookup],
                },
              },
            ],
          },
          [LABELS.OPTIONS_SORTING_KEY]: makeSorting(
            ComponentSettingKey.SelectionDataOptionsSortBy,
            ComponentSettingKey.SelectionDataOptionsSortType,
            ComponentSettingKey.SelectionDataOptionsSource
          ),
          [LABELS.OPTIONS_DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.SelectionDataOptionsFilter,
            ComponentSettingKey.SelectionDataOptionsSource
          ),
        },
        designGroup: {
          Variant: {
            items: [
              {
                id: ComponentSettingKey.SelectionDesignType,
                label: LABELS.SELECTION_TYPE_KEY,
                defaultValue: 'dropdown',
                type: BuilderComponentSettingType.IconSelect,
                // todo
                iconSelectSettings: {
                  options: (elId, ancestors, ctx) => {
                    const allOpts = [
                      {
                        id: 'dropdown',
                        icon: 'Dropdown24',
                        title: LABELS.DROPDOWN_KEY,
                      },
                      {
                        id: 'radio',
                        icon: 'Radio24',
                        title: LABELS.RADIO_KEY,
                      },
                      {
                        id: 'chip',
                        icon: 'Chip24',
                        title: LABELS.CHIP_KEY,
                      },
                      // {
                      //   id: 'checkbox',
                      //   icon: 'Checkbox24',
                      //   title: LABELS.CHECKBOX_KEY,
                      // },
                      {
                        id: 'segmented',
                        icon: 'Segmented24',
                        title: LABELS.SEGMENTED_KEY,
                      },
                    ]
                    const formParent = ancestors.find((a) => a.compId === 'form')

                    const tableId = formParent
                      ? ctx?.page?.elementSettings[formParent.id]?.[ComponentSettingKey.FormDataSource]
                      : undefined

                    const colId = ctx?.page?.elementSettings[elId]?.[ComponentSettingKey.SelectionDataValue]

                    const col = ctx?.dbTables
                      .find((tb) => tb.id === tableId)
                      ?.columns.find((c) => c.id === (colId as string))

                    const multiple = ctx?.page?.elementSettings[elId]?.[ComponentSettingKey.SelectionDataMultiple]

                    if (!col) {
                      return allOpts
                    }

                    if (col.type === ColumnTypes.Text && multiple) {
                      return allOpts.slice(2)
                    }

                    if ([ColumnTypes.Array, ColumnTypes.ImageArray].includes(col.type)) {
                      return allOpts.slice(2)
                    }

                    return allOpts.filter((c) => c.id !== 'checkbox')
                  },
                },
              },
            ],
          },
        },
        behavior: {
          custom: {
            General: {
              items: [
                {
                  id: ComponentSettingKey.SelectionBehaviorRequired,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.REQUIRED_KEY,
                },
                {
                  id: ComponentSettingKey.SelectionDataMultiple,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.MULTIPLE_KEY,
                  defaultValue: false,
                  disabledValue: true,
                  dependOn: {
                    id: ComponentSettingKey.SelectionDataValue,
                    showWhen: (value, elId, ancestors, ctx) => {
                      const formParent = ancestors.find((a) => a.compId === 'form')
                      const tableId = formParent
                        ? ctx?.page?.elementSettings[formParent.id]?.[ComponentSettingKey.FormDataSource]
                        : undefined

                      const col = ctx?.dbTables
                        .find((tb) => tb.id === tableId)
                        ?.columns.find((c) => c.id === (value as string))

                      if (!col) {
                        return false
                      }

                      if (col.type === ColumnTypes.Text) {
                        return true
                      }

                      if ([ColumnTypes.Array, ColumnTypes.ImageArray].includes(col.type)) {
                        return 'disabled'
                      }
                      //
                      return false
                    },
                  },
                },
                {
                  id: ComponentSettingKey.SelectionDataSeparator,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.SEPARATOR_KEY,
                  defaultValue: ',',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: ',',
                        label: LABELS.COMMA_KEY,
                      },
                      {
                        value: ';',
                        label: LABELS.SEMICOLON_KEY,
                      },
                      {
                        value: ':',
                        label: LABELS.COLON_KEY,
                      },
                    ],
                  },
                  dependOn: {
                    id: ComponentSettingKey.SelectionDataValue,
                    showWhen: (value, elId, ancestors, ctx) => {
                      const isMultiple = elId
                        ? (ctx?.page?.elementSettings[elId]?.[ComponentSettingKey.SelectionDataMultiple] as boolean)
                        : false
                      const formParent = ancestors.find((a) => a.compId === 'form')
                      const tableId = formParent
                        ? ctx?.page?.elementSettings[formParent.id]?.[ComponentSettingKey.FormDataSource]
                        : undefined

                      const col = ctx?.dbTables
                        .find((tb) => tb.id === tableId)
                        ?.columns.find((c) => c.id === (value as string))

                      if (!col) {
                        return false
                      }

                      if (col.type === ColumnTypes.Text) {
                        return Boolean(isMultiple)
                      }

                      return false
                    },
                  },
                },
                /*{
                  id: ComponentSettingKey.SelectionBehaviorMultiple,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Multiple',
                  help: 'Only works when Read From/Write To data column type is Array'
                }*/
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'slider',
      title: LABELS.SLIDER_KEY,
      searchKeywords: [
        'slider',
        'range',
        'scale',
        'adjuster',
        'input slider',
        'volume control',
        'rating slider',
        'progress slider',
      ],
      icon: 'Slider24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              // {
              //   id: ComponentSettingKey.SliderDataRange,
              //   type: BuilderComponentSettingType.Switch,
              //   label: LABELS.RANGE_KEY,
              // },
              {
                id: ComponentSettingKey.SliderDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                forceHorizontal: true,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.SliderDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.SliderDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.SliderDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Rating],
                },
              },
              {
                id: ComponentSettingKey.SliderDataEndValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                columnSelectSettings: {
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Rating],
                },
                dependOn: {
                  id: ComponentSettingKey.SliderDataRange,
                  showWhen: (v) => v === true,
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SliderDataDefaultValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: '',
                defaultValue: {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.CustomText,
                  value: '',
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Rating],
                },
              },
              {
                id: ComponentSettingKey.SliderDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SliderDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: 'Value' }],
                  },
                ],
              },
              {
                id: ComponentSettingKey.SliderDataHelp,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
                label: LABELS.HELP_TEXT_KEY,
              },
            ],
          },
        },
        designGroup: {
          [LABELS.GENERAL_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SliderDesignTickMark,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.STEP_MARK_KEY,
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.SliderDesignShowMinMaxValues,
                type: BuilderComponentSettingType.Switch,
                label: 'Show Min/Max Values',
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.SliderDesignShowMinMaxLabels,
                type: BuilderComponentSettingType.Switch,
                label: 'Show Min/Max Labels',
                defaultValue: false,
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SliderDesignThickness,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.HEIGHT_KEY,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  unit: 'px',
                },
              },
              {
                id: ComponentSettingKey.SliderDesignWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.WIDTH_KEY,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 0,
                  step: 1,
                  unit: '%',
                  showUnit: false,
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SliderDesignPadding,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_PADDING_KEY,
                defaultValue: getDefaultMargin([0]),
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.SliderBehaviorMinValue,
                  type: BuilderComponentSettingType.Number,
                  label: LABELS.MIN_VALUE_KEY,
                  defaultValue: 0,
                  numberSettings: {
                    max: 9999999,
                    min: 0,
                    step: 1,
                  },
                },
                {
                  id: ComponentSettingKey.SliderBehaviorMaxValue,
                  type: BuilderComponentSettingType.Number,
                  label: LABELS.MAX_VALUE_KEY,
                  defaultValue: 100,
                  numberSettings: {
                    max: 99999999,
                    min: 0,
                    step: 1,
                  },
                },
                {
                  id: ComponentSettingKey.SliderBehaviorStepValue,
                  type: BuilderComponentSettingType.Number,
                  defaultValue: 10,
                  label: LABELS.STEP_KEY,
                },
                {
                  id: ComponentSettingKey.SliderBehaviorRequired,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.REQUIRED_KEY,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'datetime-picker',
      title: LABELS.DATETIME_PICKER_KEY,
      searchKeywords: [
        'date picker',
        'time picker',
        'datetime',
        'calendar input',
        'schedule picker',
        'booking input',
        'date field',
        'time field',
      ],
      icon: 'Date24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DateTimePickerDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                forceHorizontal: true,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.DateTimePickerDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.DateTimePickerDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.DateTimePickerDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.DateTime],
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DateTimePickerDataDefaultValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: '',
                defaultValue: {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.CurrentDateTime,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.CurrentDateTime |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.DateTime],
                  inputType: 'datetime',
                },
              },
              {
                id: ComponentSettingKey.DateTimePickerDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DateTimePickerDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                defaultValue: {
                  id: uuid4(),
                  value: 'Date & Time',
                },
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
            ],
          },
        },
        // NOTE: https://trello.com/c/kXZh15st/771-bug-on-analog-time-selection
        // designGroup: {
        //   [LABELS.VIEW_KEY]: {
        //     items: [
        //       {
        //         id: ComponentSettingKey.DateTimePickerDesignTimePickerVariant,
        //         type: BuilderComponentSettingType.Select,
        //         label: LABELS.VIEW_KEY,
        //         defaultValue: 'chips',
        //         selectSettings: {
        //           options: [
        //             { value: 'chips', label: 'Chips' },
        //             { value: 'analog', label: 'Analog' },
        //           ],
        //         },
        //       },
        //     ],
        //   },
        // },
        behavior: {
          custom: {
            [LABELS.GENERAL_VALIDATION_KEY]: {
              items: [
                // {
                //   id: ComponentSettingKey.DateTimePickerBehaviorMinDate,
                //   label: 'Min. Date',
                //   type: BuilderComponentSettingType.DatePicker,
                // },
                // {
                //   id: ComponentSettingKey.DateTimePickerBehaviorMaxDate,
                //   label: 'Max. Date',
                //   type: BuilderComponentSettingType.DatePicker,
                // },
                {
                  id: ComponentSettingKey.DateTimePickerBehaviorRequired,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.REQUIRED_KEY,
                },
              ],
            },
            [LABELS.DATE_VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.DateTimePickerBehaviorFutureDateOnly,
                  label: 'Futures Date Only',
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: false,
                },
                {
                  id: ComponentSettingKey.DateTimePickerBehaviorDaysAllowed,
                  label: 'Specific Days Allowed',
                  type: BuilderComponentSettingType.Select,
                  defaultValue: [0, 1, 2, 3, 4, 5, 6],
                  selectSettings: {
                    multiple: true,
                    type: 'chip',
                    options: [
                      { value: 0, label: 'Sun' },
                      { value: 1, label: 'Mon' },
                      { value: 2, label: 'Tue' },
                      { value: 3, label: 'Wed' },
                      { value: 4, label: 'Thu' },
                      { value: 5, label: 'Fri' },
                      { value: 6, label: 'Sat' },
                    ],
                  },
                },
              ],
            },
            [LABELS.TIME_VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.DateTimePickerBehaviorTimeInterval,
                  label: 'Time Interval (Minutes)',
                  type: BuilderComponentSettingType.Select,
                  defaultValue: '15',
                  selectSettings: {
                    options: [
                      { value: '1', label: '1 min' },
                      { value: '5', label: '5 min' },
                      { value: '10', label: '10 min' },
                      { value: '15', label: '15 min' },
                      { value: '30', label: '30 min' },
                      { value: '60', label: '60 min' },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.DateTimePickerBehaviorStartTime,
                  label: 'Start Time',
                  type: BuilderComponentSettingType.Select,
                  defaultValue: '00:00',
                  selectSettings: {
                    options: generateTimeOptions('12', 'start'),
                  },
                },
                {
                  id: ComponentSettingKey.DateTimePickerBehaviorEndTime,
                  label: 'End Time',
                  type: BuilderComponentSettingType.Select,
                  defaultValue: '24:00',
                  selectSettings: {
                    options: generateTimeOptions('12', 'end'),
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'file',
      title: LABELS.FILE_UPLOAD_KEY,
      searchKeywords: ['file upload', 'document upload', 'attachment', 'file picker', 'pdf upload', 'drag & drop file'],
      icon: 'AttachFile16',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FileUploadDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.FileUploadDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.FileUploadDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.FileUploadDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Attachment, ColumnTypes.Image],
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FileUploadDataDefaultValue,
                label: '',
                type: BuilderComponentSettingType.VariableSelect,
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.Image |
                    VariableSelectFeatureFlag.File |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Attachment],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FileUploadDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
              {
                id: ComponentSettingKey.FileUploadDataHelp,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
                label: LABELS.HELP_TEXT_KEY,
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.FileUploadBehaviorShowHelpText,
                  label: LABELS.SHOW_HELP_TEXT_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.FileUploadBehaviorMaxSize,
                  type: BuilderComponentSettingType.NumberWithUnit,
                  label: LABELS.MAX_SIZE_KEY,
                  help: 'Maximum allowed size is 5GB',
                  defaultValue: {
                    value: 5,
                    unit: 'mb',
                  },
                  numberWithUnitSettings: {
                    min: 0,
                    step: 5,
                    options: [
                      {
                        value: 'kb',
                        label: 'KB',
                        max: 1000,
                      },
                      {
                        value: 'mb',
                        label: 'MB',
                        max: 1000,
                      },
                      {
                        value: 'gb',
                        label: 'GB',
                        max: 5,
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.FileUploadBehaviorRequired,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.REQUIRED_KEY,
                },
                {
                  id: ComponentSettingKey.FileUploadBehaviorAllowedFileTypes,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.ALLOWED_FILE_TYPES_KEY,
                  defaultValue: DEFAULT_FILE_TYPES,
                  selectSettings: {
                    multiple: true,
                    options: ALLOWED_FILE_TYPES.map((o) => {
                      return {
                        value: o,
                        label: o,
                      }
                    }),
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'image-upload',
      title: LABELS.IMAGE_UPLOAD_KEY,
      searchKeywords: ['image upload', 'photo upload', 'avatar upload', 'profile picture', 'drag & drop image'],
      icon: 'UploadImage24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageUploadDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.ImageUploadDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.ImageUploadDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.ImageUploadDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Attachment, ColumnTypes.ImageArray],
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageUploadDataDefaultValue,
                label: '',
                type: BuilderComponentSettingType.VariableSelect,
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.Image |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Attachment],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageUploadDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
              {
                id: ComponentSettingKey.ImageUploadDataHelp,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.HELP_TEXT_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.ImageUploadBehaviorShowHelpText,
                  label: LABELS.SHOW_HELP_TEXT_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.ImageUploadBehaviorMaxItems,
                  label: LABELS.MAX_ITEMS_KEY,
                  type: BuilderComponentSettingType.Number,
                  defaultValue: 5,
                  numberSettings: {
                    max: 20,
                    min: 1,
                    step: 1,
                  },
                  dependOn: {
                    id: ComponentSettingKey.ImageUploadDataValue,
                    showWhen: (val, elId, ancestors, context, formContext) => {
                      const dbTable = formContext?.dbTable
                      const columnId = val || ''
                      const column = dbTable?.columnsMap.get(columnId as string)
                      const columnType = column?._Type

                      return columnType === ColumnTypes.ImageArray
                    },
                  },
                },
                {
                  id: ComponentSettingKey.ImageUploadBehaviorMaxSize,
                  type: BuilderComponentSettingType.NumberWithUnit,
                  help: 'Maximum allowed size is 1GB',
                  label: LABELS.MAX_SIZE_KEY,
                  defaultValue: {
                    value: 1,
                    unit: 'gb',
                  },
                  numberWithUnitSettings: {
                    min: 0,
                    step: 10,
                    options: [
                      {
                        value: 'kb',
                        label: 'KB',
                        max: 1000,
                      },
                      {
                        value: 'mb',
                        label: 'MB',
                        max: 1000,
                      },
                      {
                        value: 'gb',
                        label: 'GB',
                        max: 5,
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.ImageUploadBehaviorRequired,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.REQUIRED_KEY,
                },
                {
                  id: ComponentSettingKey.ImageUploadBehaviorAllowedFileTypes,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.ALLOWED_FILE_TYPES_KEY,
                  defaultValue: DEFAULT_IMAGE_TYPES,
                  selectSettings: {
                    multiple: true,
                    options: ALLOWED_IMAGE_TYPES.map((o) => {
                      return {
                        value: o,
                        label: o,
                      }
                    }),
                  },
                },
              ],
            },
            [LABELS.IMAGE_COMPRESSION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.ImageUploadBehaviorCompressionMaxSize,
                  label: LABELS.IMAGE_MAX_SIZE_KEY,
                  help: 'The maximum size of the compressed image in kb',
                  type: BuilderComponentSettingType.Slider,
                  defaultValue: [150],
                  sliderSettings: {
                    max: 1024,
                    min: 100,
                    step: 1,
                    range: false,
                    unit: 'kb',
                    showUnit: false,
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'icon-picker',
      title: 'Icon Picker',
      searchKeywords: ['icon picker', 'icon selector', 'icon selector', 'icon selector'],
      icon: 'IconPicker24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.IconPickerDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                columnSelectSettings: {
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Icon],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.IconPickerDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                  shouldUpdateComponentTitle: true,
                  maxChars: 100,
                },
              },
              {
                id: ComponentSettingKey.IconPickerDataHelp,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.HELP_TEXT_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
              },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },
    {
      compId: 'hidden-field',
      title: LABELS.HIDDEN_FIELD_KEY,
      searchKeywords: ['hidden field', 'invisible input', 'metadata', 'background field', 'system field'],
      icon: 'EyeSlashFilled24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.HiddenFieldDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                forceHorizontal: true,
                columnSelectSettings: {
                  onValueChangeV2: (nextVal, prevSettings) => {
                    const prevDefaultValue = prevSettings[
                      ComponentSettingKey.HiddenFieldDataDefaultValue
                    ] as VariableResultCustomText

                    if (prevDefaultValue) {
                      prevSettings[ComponentSettingKey.HiddenFieldDataDefaultValue] = {
                        id: prevDefaultValue.id,
                        value: '',
                        flag: prevDefaultValue.flag,
                      }
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.HiddenFieldDataAttribute],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [...DEFAULT_COLUMN_TYPES, ColumnTypes.Image, ColumnTypes.Attachment, ColumnTypes.URL],
                  noAutoSelect: true,
                },
              },
              // {
              //   id: ComponentSettingKey.HiddenFieldDataAttribute,
              //   type: BuilderComponentSettingType.VariableText,
              //   variableTextSettings: {
              //     flags: VariableTextFeatureFlag.SingleCell,
              //     columnTypes: [ColumnTypes.Text],
              //   },
              //   label: LABELS.DATA_ATTRIBUTE_KEY,
              // },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.HiddenFieldDataDefaultValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: '',
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.Icon |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID |
                    VariableSelectFeatureFlag.CurrentDateTime,
                  bindForm: true,
                },
              },
            ],
          },
        },
      },
    },
    {
      compId: 'phone-input',
      title: 'Phone Input',
      searchKeywords: ['phone input', 'phone number', 'phone number input', 'phone number field'],
      icon: 'PhoneInput24',
      nested: false,
      isForm: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PhoneInputDataValue,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLUMN_KEY,
                forceHorizontal: true,
                columnSelectSettings: {
                  onValueChangeV2: (_, prevSettings) => {
                    const newLabel = prevSettings[ComponentSettingKey.PhoneInputDataLabel]
                    if (newLabel) {
                      prevSettings[ComponentSettingKey.PhoneInputDataLabel] = getDefaultDescendantValue(newLabel)
                    }
                  },
                  dependentKeysOnChange: [ComponentSettingKey.PhoneInputDataLabel],
                  tableSourceId: '',
                  bindForm: true,
                  columnTypes: [ColumnTypes.Phone],
                },
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PhoneInputDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.MAIN_LABEL_KEY,
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Phone],
                  shouldUpdateComponentTitle: true,
                  maxChars: 15,
                },
              },
            ],
          },
          [LABELS.DEFAULT_VALUE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PhoneInputDataDefaultValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: '',
                defaultValue: {
                  id: uuid4(),
                  value: '',
                  flag: VariableSelectFeatureFlag.CustomText,
                },
                autoDefaultInDetailPage: false,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.CustomText |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.SessionID |
                    VariableSelectFeatureFlag.UniqueID,
                  bindForm: true,
                  formValidation: ComponentSettingKey.PhoneInputBehaviorPattern,
                },
              },
              {
                id: ComponentSettingKey.PhoneInputDataDefaultValueEditable,
                type: BuilderComponentSettingType.Switch,
                label: 'Editable',
                defaultValue: true,
              },
            ],
          },
        },
        behavior: {
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PhoneInputBehaviorPattern,
                  label: LABELS.PATTERN_KEY,
                  type: BuilderComponentSettingType.Select,
                  selectSettings: {
                    multiline: true,
                    options: FORM_VALIDATION_PATTERNS.filter((item) =>
                      [
                        'integer',
                        'positive_integer',
                        'negative_integer',
                        'decimal',
                        'positive_decimal',
                        'negative_decimal',
                        'phone_basic',
                        'phone_us',
                        'phone_international',
                      ].includes(item.id)
                    ).map((item) => ({
                      value: item.pattern,
                      label: item.label,
                    })),
                  },
                  defaultValue: FORM_VALIDATION_PATTERNS.find((p) => p.id === 'phone_international')?.pattern || 'none',
                },
                {
                  id: ComponentSettingKey.PhoneInputBehaviorPatternShowHelp,
                  label: LABELS.SHOW_VALIDATION_HELP_TEXT_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.PhoneInputBehaviorMinChar,
                  label: LABELS.MIN_CHARACTERS_KEY,
                  type: BuilderComponentSettingType.Number,
                  defaultValue: 1,
                },
                {
                  id: ComponentSettingKey.PhoneInputBehaviorMaxChar,
                  label: LABELS.MAX_CHARACTERS_KEY,
                  type: BuilderComponentSettingType.Number,
                  defaultValue: 12,
                  numberSettings: {
                    max: 15,
                    min: 0,
                    step: 1,
                  },
                },
                {
                  id: ComponentSettingKey.PhoneInputBehaviorRequired,
                  label: LABELS.REQUIRED_KEY,
                  type: BuilderComponentSettingType.Switch,
                },
              ],
            },
            [LABELS.SHOW_HIDE_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PhoneInputBehaviorCharacterCounter,
                  label: 'Character Counter',
                  help: 'Enable this will replace help text.',
                  type: BuilderComponentSettingType.Switch,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
  ],
}
